import React, { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { AppContext } from 'Context/AppContext';
import {
    DATA_CONFIGURATION,
    DEFAULT_VALUE,
    ENTER,
    FIELD_CLASS,
    INCREMENT,
    LABEL,
    MANAGE,
    MAX,
    MIN,
} from '../ISRConstants';
import {
    TConnector,
    TField,
    TFieldClass,
    TFieldDataConfiguration, TIndexState,
    TProductCategory,
    TSubmodel,
    TUpsertISRProjectDataModelsInput
} from '../Pages/DefineModel/model';
import { useISRContext } from '../Hooks/useISRContext';
import { getAllProjectClass } from '../Pages/DefineModel/ModelServices/ModalServices';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import Card from 'Components/DesignSystem/Card/Card';
import TextField from 'Components/DesignSystem/TextField/TextField';
import Typography from 'Components/DesignSystem/Typography/Typography';
import ConfigurationFilterComponent from './ConfigurationFilterComponent';
import FieldClassSelector from './FieldClassSelector';

type TIntegerConfigurationProps = {
    id: string;
    selectedItem: TField | TSubmodel | TProductCategory | TConnector | null;
    updateFieldDataConfiguration: (updatedConfig: TFieldDataConfiguration) => void;
    projectId?: string | null | undefined;
    userProjectDataModels: TUpsertISRProjectDataModelsInput;
    indexInfo: TIndexState;
}

const IntegerConfiguration = ({id, selectedItem, updateFieldDataConfiguration, projectId, userProjectDataModels, indexInfo}: TIntegerConfigurationProps) => {
    const [fieldDataConfiguration, setFieldDataConfiguration] = useState<TFieldDataConfiguration>({});
    const [minMaxError, setMinMaxError] = useState<string>('');
    const location = useLocation();
    const isGuide = location.pathname.includes('/guide');
    const { enableConfigure } = useContext(AppContext);
    const isConfigureDisabled = isGuide && !enableConfigure;
    const isrContext = useISRContext();

    const getMinMaxError = (min: number | string | undefined, max: number | string | undefined): string => {
        const isEmpty = (value: number | string | undefined) => value === undefined || value === null || value === '';
        // Mirror the displayed input defaults (min ?? 0, max ?? 1) so validation matches what the user sees.
        const minVal = isEmpty(min) ? 0 : Number(min);
        const maxVal = isEmpty(max) ? 1 : Number(max);
        return minVal > maxVal ? 'Minimum value cannot be greater than the maximum.' : '';
    };


    useEffect(() => {
        const existingValue = selectedItem && 'dataConfiguration' in selectedItem && selectedItem?.dataConfiguration ? selectedItem.dataConfiguration : {}
        setFieldDataConfiguration(existingValue);
    }, [selectedItem]);

    useEffect(() => {
        const error = getMinMaxError(fieldDataConfiguration?.min, fieldDataConfiguration?.max);
        setMinMaxError(error);
        const hasChanges = Object.keys(fieldDataConfiguration).some(
            (key) => fieldDataConfiguration[key as keyof TFieldDataConfiguration] !== undefined
        );
        if (hasChanges && !error) {
            updateFieldDataConfiguration(fieldDataConfiguration);
        }
    }, [fieldDataConfiguration]);

    const handleMinChange = (value: string) => {
        setFieldDataConfiguration((prevConfig) => ({...prevConfig, min: value === '' ? undefined : Math.max(0, Number(value))}));
    };

    const handleMaxChange = (value: string) => {
        setFieldDataConfiguration((prevConfig) => ({...prevConfig, max: value === '' ? undefined : Math.max(0, Number(value))}));
    };

    const handleIncrementChange = (value: number) => {
        setFieldDataConfiguration((prevConfig) => ({...prevConfig, increment: value}));
    };

    const handleLabelChange = (value: string) => {
        setFieldDataConfiguration((prevConfig) => ({...prevConfig, label: value}));
    };

    const handleDefaultValueChange = (value: string) => {
        setFieldDataConfiguration((prevConfig) => ({...prevConfig, defaultValue: value.length > 0 ? Number(value): value}));
    };

    const handleClassChange = (selectedValue: string) => {
        const selectedObject = isrContext?.fieldClasses?.find((item: TFieldClass) => item.name === selectedValue);
        setFieldDataConfiguration((prevConfig) => ({...prevConfig, class: selectedObject}));
    };

    useEffect(() => {
        fetchAllClasses();
    }, []);

    const fetchAllClasses = async () => {
        try {
            const responseData = await getAllProjectClass(projectId ?? '');
            if (responseData?.done) {
                isrContext?.setFieldClasses(responseData?.data?.fieldClasses ?? []);
                isrContext?.setModelClasses(responseData?.data?.modelClasses ?? []);
            } else {
                toast.error(responseData?.message);
            }
        } catch (error) {
            console.log('Error', error);
        }
    }

    return (
        <Card id={id} data-testid={id} title={DATA_CONFIGURATION}>
            <div id={`${id}-range-section`} className='tw-flex tw-items-start tw-gap-5'>
                <div id={`${id}-min-container`} className='tw-w-[15%]'>
                    <TextField
                        id={`${id}-min-input`}
                        data-testid={`${id}-min-input`}
                        label={MIN}
                        placeholder={ENTER}
                        type='number'
                        minValue={0}
                        error={!!minMaxError}
                        onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }}
                        onChange={handleMinChange}
                        value={String(fieldDataConfiguration?.min ?? 0)}
                    />
                    {minMaxError && (
                        <Typography
                            variant="caption-metadata"
                            as="p"
                            id={`${id}-min-max-error`}
                            data-testid={`${id}-min-max-error`}
                            className='tw-text-tone-danger-ink tw-mt-1 tw-w-max tw-max-w-[12rem] tw-break-words'
                        >
                            {minMaxError}
                        </Typography>
                    )}
                </div>
                <TextField
                    id={`${id}-max-input`}
                    data-testid={`${id}-max-input`}
                    label={MAX}
                    placeholder={ENTER}
                    type='number'
                    minValue={0}
                    error={!!minMaxError}
                    onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }}
                    onChange={handleMaxChange}
                    value={String(fieldDataConfiguration?.max ?? 1)}
                    className='tw-w-[15%]'
                />
            </div>
            <div id={`${id}-settings-section`} className='tw-flex tw-items-start tw-gap-5 tw-mt-3'>
                <TextField
                    id={`${id}-increment-input`}
                    data-testid={`${id}-increment-input`}
                    label={INCREMENT}
                    placeholder={ENTER}
                    type='number'
                    onChange={(value) => handleIncrementChange(Number(value))}
                    value={String(fieldDataConfiguration?.increment ?? '')}
                    className='tw-w-[15%]'
                />
                <TextField
                    id={`${id}-label-input`}
                    data-testid={`${id}-label-input`}
                    label={LABEL}
                    placeholder={ENTER}
                    onChange={handleLabelChange}
                    value={fieldDataConfiguration?.label ?? ''}
                    className='tw-w-[30%]'
                />
                <TextField
                    id={`${id}-default-value-input`}
                    data-testid={`${id}-default-value-input`}
                    label={DEFAULT_VALUE}
                    placeholder={ENTER}
                    type='number'
                    onChange={handleDefaultValueChange}
                    value={String(fieldDataConfiguration?.defaultValue ?? '')}
                    className='tw-w-[15%]'
                />
            </div>

            <ConfigurationFilterComponent
                id={`${id}-config-filter`}
                fieldDataConfiguration={fieldDataConfiguration}
                setFieldDataConfiguration={setFieldDataConfiguration}
                userProjectDataModels={userProjectDataModels}
                indexInfo={indexInfo}
            />

            <FieldClassSelector
                baseId={id}
                value={fieldDataConfiguration?.class?.name ?? ''}
                onChange={handleClassChange}
                disabled={isConfigureDisabled}
                projectId={projectId}
            />
        </Card>
    );
}

export default IntegerConfiguration;